import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, PageHeader, SectionCard } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';
import { compressImage } from '../utils/compressImage';

type LeadRow = {
  id: number;
  nome: string;
  tipologia: string;
  google_maps_url: string;
  latitudine: string | null;
  longitudine: string | null;
  indirizzo_reale: string | null;
  categoria_reale: string | null;
  telefono: string | null;
  sito_web: string | null;
  creato_il: string;
  comune_nome: string;
};

export function MasterPortaleScraperPage() {
  const [righe, setRighe] = useState<LeadRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fotoFiles, setFotoFiles] = useState<Record<number, File | null>>({});

  // --- Scraper search state ---
  const [comuni, setComuni] = useState<string[]>([]);
  const [categorie, setCategorie] = useState<string[]>([]);
  const [searchComune, setSearchComune] = useState('');
  const [searchCategoria, setSearchCategoria] = useState('');
  const [searchMax, setSearchMax] = useState('20');
  const [includePhone, setIncludePhone] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Array<{
    nome: string;
    indirizzo: string | null;
    categoria_reale: string | null;
    telefono: string | null;
    sito_web: string | null;
    google_maps_url: string;
    already_exists: boolean;
  }>>([]);
  const [searchStats, setSearchStats] = useState<{ total_found: number; saved: number; skipped: number; errors: number; dry_run: boolean } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await apiFetch('/api/v1/portale/master/scansionate');
      const body = (await res.json().catch(() => [])) as LeadRow[] | { error?: string };
      if (!res.ok) throw new Error((body as { error?: string })?.error || `HTTP ${res.status}`);
      setRighe(Array.isArray(body) ? body : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Load scraper options (comuni + categorie)
  useEffect(() => {
    void (async () => {
      try {
        const res = await apiFetch('/api/v1/portale/master/scraper/options');
        const body = (await res.json().catch(() => ({}))) as { comuni?: string[]; categorie?: string[] };
        if (res.ok) {
          setComuni(body.comuni || []);
          setCategorie(body.categorie || []);
          if (body.comuni?.[0]) setSearchComune(body.comuni[0]);
          if (body.categorie?.[0]) setSearchCategoria(body.categorie[0]);
        }
      } catch {
        // soft-fail
      }
    })();
  }, []);

  async function runSearch() {
    setSearching(true);
    setError(null);
    setMessage(null);
    setSearchResults([]);
    setSearchStats(null);
    try {
      const res = await apiFetch('/api/v1/portale/master/scraper/search', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          comune: searchComune,
          categoria: searchCategoria,
          maxResults: Number(searchMax) || 20,
          includePhone,
          dryRun,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        results?: typeof searchResults;
        total_found?: number;
        saved?: number;
        skipped?: number;
        errors?: number;
        dry_run?: boolean;
        error?: string;
      };
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setSearchResults(body.results || []);
      setSearchStats({
        total_found: body.total_found || 0,
        saved: body.saved || 0,
        skipped: body.skipped || 0,
        errors: body.errors || 0,
        dry_run: Boolean(body.dry_run),
      });
      if (!body.dry_run && (body.saved || 0) > 0) {
        setMessage(`${body.saved} attività salvate. ${body.skipped} saltate (duplicati).`);
        await load();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSearching(false);
    }
  }

  async function promuovi(row: LeadRow) {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      let foto: string | null = null;
      const file = fotoFiles[row.id];
      if (file) {
        const compressed = await compressImage(file);
        const formData = new FormData();
        formData.append('image', compressed);
        const upRes = await apiFetch('/api/v1/portale/master/upload-foto', {
          method: 'POST',
          body: formData,
        });
        const upBody = (await upRes.json().catch(() => ({}))) as { foto?: string; error?: string };
        if (!upRes.ok) throw new Error(upBody?.error || `upload ${upRes.status}`);
        foto = upBody.foto || null;
      }

      const res = await apiFetch(`/api/v1/portale/master/scansionate/${row.id}/promuovi`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ foto }),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setMessage(`Card generata per "${row.nome}"`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function scarta(row: LeadRow) {
    if (!window.confirm(`Scartare definitivamente "${row.nome}" dalla lista dei lead?`)) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await apiFetch(`/api/v1/portale/master/scansionate/${row.id}`, {
        method: 'DELETE',
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setMessage(`Lead "${row.nome}" scartato`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Portale · Lead scraper"
        subtitle={
          <>
            <Link to="/master">← Master</Link> · attività geolocalizzate raccolte in background.
            Associa una foto e genera la card, oppure scarta i doppioni.
          </>
        }
      />

      {message ? <p className="sk-ok">{message}</p> : null}
      {error ? <p className="sk-error">{error}</p> : null}

      <SectionCard title="Ricerca attività (Google Places API)">
        <p className="sk-mc-hint">
          Cerca attività commerciali via Google Places API (conforme ToS Google).
          I risultati vengono salvati automaticamente come lead.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' }}>
          <label>
            <span className="sk-muted">Comune</span>
            <select value={searchComune} onChange={(e) => setSearchComune(e.target.value)} style={{ width: '100%', padding: '8px' }}>
              {comuni.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>
            <span className="sk-muted">Categoria</span>
            <select value={searchCategoria} onChange={(e) => setSearchCategoria(e.target.value)} style={{ width: '100%', padding: '8px' }}>
              {categorie.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>
            <span className="sk-muted">Max risultati (1-60)</span>
            <input type="number" min={1} max={60} value={searchMax} onChange={(e) => setSearchMax(e.target.value)} style={{ width: '100%', padding: '8px' }} />
          </label>
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input type="checkbox" checked={includePhone} onChange={(e) => setIncludePhone(e.target.checked)} />
            <span>Raccogli telefono (Place Details — costa di più)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
            <span>Dry run (solo anteprima, non salvare)</span>
          </label>
          <button
            onClick={() => void runSearch()}
            disabled={searching || !searchComune || !searchCategoria}
            className="sk-btn sk-btn--primary"
          >
            {searching ? 'Ricerca in corso...' : 'Cerca attività'}
          </button>
        </div>

        {searchStats ? (
          <p className="sk-muted" style={{ marginBottom: '12px' }}>
            Trovate: <strong>{searchStats.total_found}</strong> ·
            Salvate: <strong>{searchStats.saved}</strong> ·
            Saltate: <strong>{searchStats.skipped}</strong> ·
            Errori: <strong>{searchStats.errors}</strong>
            {searchStats.dry_run ? ' · (DRY RUN — nessun salvataggio)' : ''}
          </p>
        ) : null}

        {searchResults.length > 0 ? (
          <DataTable
            emptyMessage="Nessun risultato"
            rows={searchResults.map((r, i) => ({ ...r, row_key: String(i) }))}
            columns={[
              {
                key: 'nome',
                label: 'Nome',
                render: (_v, row) => (
                  <span>
                    <strong>{String(row.nome)}</strong>
                    {row.already_exists ? <span className="sk-muted"> (già presente)</span> : null}
                  </span>
                ),
              },
              { key: 'indirizzo', label: 'Indirizzo', render: (v) => v ? String(v) : '—' },
              { key: 'categoria_reale', label: 'Categoria', render: (v) => v ? String(v) : '—' },
              { key: 'telefono', label: 'Telefono', render: (v) => v ? String(v) : '—' },
              {
                key: 'google_maps_url',
                label: 'Maps',
                render: (v) => v ? <a href={String(v)} target="_blank" rel="noreferrer">Vedi →</a> : '—',
              },
            ]}
          />
        ) : null}
      </SectionCard>

      <SectionCard title={`${righe.length} lead da elaborare`}>
        <DataTable
          emptyMessage="Nessuna nuova attività scansionata da elaborare"
          rows={righe.map((r) => ({ ...r, row_key: String(r.id) }))}
          columns={[
            {
              key: 'nome',
              label: 'Insegna',
              render: (_v, row) => (
                <span>
                  <strong>{String(row.nome)}</strong>
                  {row.indirizzo_reale ? (
                    <span className="sk-muted"> · {String(row.indirizzo_reale)}</span>
                  ) : null}
                </span>
              ),
            },
            { key: 'tipologia', label: 'Tipologia' },
            { key: 'comune_nome', label: 'Comune' },
            {
              key: 'google_maps_url',
              label: 'Maps',
              render: (v) =>
                v ? (
                  <a href={String(v)} target="_blank" rel="noreferrer">
                    Vedi scheda →
                  </a>
                ) : (
                  '—'
                ),
            },
            {
              key: 'foto',
              label: 'Immagine card',
              render: (_v, row) => (
                <input
                  type="file"
                  accept="image/*"
                  style={{ maxWidth: 170, fontSize: '0.8rem' }}
                  onChange={(e) =>
                    setFotoFiles((prev) => ({
                      ...prev,
                      [Number(row.id)]: e.target.files?.[0] || null,
                    }))
                  }
                  disabled={busy}
                />
              ),
            },
            {
              key: 'actions',
              label: '',
              render: (_v, row) => (
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button
                    type="button"
                    className="sk-btn sk-btn--sm"
                    disabled={busy}
                    onClick={() => void promuovi(row as unknown as LeadRow)}
                  >
                    Genera card
                  </button>
                  <button
                    type="button"
                    className="sk-btn sk-btn--quiet sk-btn--sm"
                    disabled={busy}
                    onClick={() => void scarta(row as unknown as LeadRow)}
                  >
                    Scarta
                  </button>
                </div>
              ),
            },
          ]}
        />
      </SectionCard>
    </section>
  );
}
